import { useTranslation } from 'react-i18next';
import { QueryKey as TQueryKey, useQuery } from '@tanstack/react-query';
import type { UseMutationOptions } from '@tanstack/react-query/src/types';
import {
    getBudgetBuckets,
    createBudgetBucket,
    updateBudgetBucket,
    deleteBudgetBucket,
    BudgetBucket,
} from '../../services/budgetBucket';
import { QueryKey } from '../../enums/QueryKey';
import { queryClient } from '../../services/queryClient';
import { useCreateRecord, useUpdateRecord, useDeleteRecord } from './crudOperations';

export function useBudgetBuckets(): [BudgetBucket[], boolean] {
    const queryKey = [QueryKey.BudgetBuckets] as TQueryKey;

    const { data, isLoading, isFetching, isFetchedAfterMount } = useQuery<BudgetBucket[], Error>({
        queryKey,
        queryFn: () => getBudgetBuckets(),
        initialData: [],
    });

    return [data, isFetchedAfterMount ? isLoading : isFetching];
}

export function useCreateBudgetBucket({
    onSettled,
}: Pick<UseMutationOptions<BudgetBucket, Error, Parameters<typeof createBudgetBucket>[0]>, 'onSettled'> = {}) {
    const { t } = useTranslation('budget');
    return useCreateRecord<BudgetBucket, Error, Parameters<typeof createBudgetBucket>[0]>({
        createRecordFn: createBudgetBucket,
        fetchRecordsQueryKey: [QueryKey.BudgetBuckets],
        recordName: t('entity.bucket'),
        onSettled,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetBuckets] });
            // CALL-758: creating a bucket also auto-creates its draft version — refetch versions.
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetVersions] });
        },
    });
}

export function useUpdateBudgetBucket({
    onSettled,
}: Pick<UseMutationOptions<BudgetBucket, Error, Parameters<typeof updateBudgetBucket>[0]>, 'onSettled'> = {}) {
    const { t } = useTranslation('budget');
    return useUpdateRecord<BudgetBucket, Error, Parameters<typeof updateBudgetBucket>[0]>({
        updateRecordFn: updateBudgetBucket,
        fetchRecordsQueryKey: [QueryKey.BudgetBuckets],
        recordName: t('entity.bucket'),
        onSettled,
    });
}

// A delete blocked by versions/configs in the bucket returns a 4xx ValidationError; useDeleteRecord
// surfaces error.message via the error notification, so the "in use" reason is shown as a toast.
export function useDeleteBudgetBucket({
    onSettled,
}: Pick<UseMutationOptions<unknown, Error, number>, 'onSettled'> = {}) {
    const { t } = useTranslation('budget');
    return useDeleteRecord<unknown, Error, number>({
        deleteRecordFn: deleteBudgetBucket,
        fetchRecordsQueryKey: [QueryKey.BudgetBuckets],
        recordName: t('entity.bucket'),
        onSettled,
    });
}
